import {textToHtml} from '@frontapp/editor-html';
import {t, Trans} from '@lingui/macro';
import _ from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, RefObject, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isHtml} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {textSelection} from '../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {
  buildCalendarEventVersionLocationText,
  buildCalendarEventVersionRemindersToSave,
  buildDefaultShowAsForIsAllDay,
  calendarCanEditGlobalSettings,
  CalendarEventTimeTypesEnum,
  isEventEditable,
  isUnsavedDraftCalendarEventVersion,
  makeCalendarEventVersionColor,
  makeCalendarEventVersionDateTime,
  makeStartEndDateTimeForCalendarEventVersion,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {
  buildCalendarEventVersionColor,
  buildCalendarEventVersionStartEnd,
  StartEndData,
} from '../../../helpers/calendarHelpers';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {addOrganizerToVisualParticipants} from '../../../helpers/models/calendarEventHelpers';
import {isProviderConferenceType, tryFindConferenceJoinUrl} from '../../../helpers/models/conferenceHelpers';
import {
  isRecurringExceptionEvent,
  isRecurringMasterEvent,
  isRecurringSaveTypeRequired,
} from '../../../helpers/models/recurringEventHelpers';
import {
  CalendarEventVersionModel,
  CalendarEventVersionReminder,
  CalendarEventVersionShowAsTypesEnum,
  CalendarEventVersionVisibilityTypesEnum,
} from '../../../models/api/calendarEventVersionModel';
import {CalendarModel, CalendarProviderTypesEnum} from '../../../models/api/calendarModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {ConferenceModel} from '../../../models/api/conferenceModel';
import {ParticipantTypesEnum} from '../../../models/api/participantModel';
import {RecurrenceModel} from '../../../models/api/recurrenceModel';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {CalendarEventSaveData} from '../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {
  CalendarEventOverrides,
  ViewingNowCalendar,
} from '../../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId, makeCalendarsPanelId} from '../../../store/panels/panelStateBase';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {HtmlEditorBoxHandle} from '../../common/boxedInputs/htmlEditorBox';
import {HtmlEditorBoxContainer} from '../../common/boxedInputs/htmlEditorBoxContainer';
import {OkCancelDialog} from '../../common/dialogs/okCancelDialog';
import {CalendarEventTimeFormatter} from '../../common/formatters/calendarEventTimeFormatter';
import {DynamicScrollBoundaries} from '../../common/scroll/dynamicScrollBoundaries';
import {buildCalendarEventDescriptionHtml} from '../calendarEventHelpers';
import {SideCalendarsHeader} from '../header/sideCalendarsHeader';
import {CalendarRemindersEditor} from '../reminders/calendarRemindersEditor';
import {InstantReschedulingLinkDialogContainer} from '../scheduling/instantRescheduling/instantReschedulingLinkDialogContainer';
import {CalendarEventDetailRow} from '../viewer/calendarEventDetailRow';
import {CalendarColorDropdownBox} from './calendarColorDropdownBox';
import {SelectedCalendarData} from './calendarDropdownBox';
import {CalendarDropdownBoxContainer} from './calendarDropdownBoxContainer';
import {OnCalendarEventEditorDoneArgs} from './calendarEventEditorContainer';
import {CalendarEventStartEndEditor} from './calendarEventStartEndEditor';
import {CalendarShowAsDropdownBox} from './calendarShowAsDropdownBox';
import {CalendarVisibilityDropdownBox} from './calendarVisibilityDropdownBox';
import {ConferenceEditor} from './conferenceEditor/conferenceEditor';
import {MeetingRoomsEditor} from './meetingRoomsEditor/meetingRoomsEditor';
import {CalendarEventParticipantsEditor} from './participantsEditor/calendarEventParticipantsEditor';
import {RecurringUpdateDialog} from './recurring/recurringUpdateDialog';

/*
 * Props.
 */

interface CalendarEventEditorProps {
  panelId: CalendarsPanelId;
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>;
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  instantReschedulingLink: SchedulingLinkModel | undefined;
  isEventOrganizer: boolean;
  timeFormat: TimeFormatsEnum;
  fetchOrCreateInstantReschedulingLinkAsync: FetchOrCreateInstantReschedulingLinkType;
  onBackClick?: Handler;
  onRequestClose?: Handler;
  onSaveClick: HandlerOf<CalendarEventSaveData>;
  onCalendarEventEditorDone: HandlerOf<OnCalendarEventEditorDoneArgs>;
  onFocusedEventOverridesChange: HandlerOf<CalendarEventOverrides> | undefined;
  onVisualParticipantsChange?: (
    oldVisualParticipants: ReadonlyArray<VisualParticipantModel>,
    newVisualParticipants: ReadonlyArray<VisualParticipantModel>,
  ) => void;
  onViewGuestAvailabilityClick?: HandlerOf<ReadonlyArray<VisualParticipantModel>> | undefined;
  masterCalendarEventVersion?: CalendarEventVersionModel;
  hasDefaultVideoConferencing: boolean;
  hasInstantReschedulingLinkFF: boolean;
  isInstantReschedulingEnabledPreference: boolean;
}

type FetchOrCreateInstantReschedulingLinkType = (calendarVersionId: number) => Promise<void>;

/*
 * Styles.
 */

const StyledInstantReschedulingWrapper = styled.div`
  padding-left: 10px;
  padding-left: 29px;
  margin-top: -12px;
  display: grid;
  grid-template-rows: auto auto;
`;

const StyledHeaderSettingsDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
`;

const StyledInstantReschedulingTitleDiv = styled.div`
  padding-left: 15px;
  display: grid;
  grid-template-columns: auto 1fr;
`;

const StyledSetAvailableTimesWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  margin-left: 17px;
  padding-top: 5px;
`;

const StyledInstantReschedulingToolTipDiv = styled.div`
  margin-left: 5px;
`;

const StyledLayoutDiv = styled.div`
  grid-template-rows: auto 1fr auto;
  display: grid;
  overflow: hidden;
`;

const StyledWrapperDiv = styled.div`
  padding: 2px 15px 0px 15px;
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 12px;
  overflow-y: scroll;
`;

const StyledFooterDiv = styled.div`
  position: sticky;
  bottom: 0px;
  background: ${(p) => p.theme.greys.white};

  display: grid;
  grid-gap: 12px;
  grid-template-columns: auto auto;
  justify-content: end;

  padding: 16px;
`;

const StyledDateSectionWrapperDiv = styled.div`
  margin-top: 12px;
`;

const StyledSectionIcon = styled(Icon)`
  margin-top: 7px;
`;

const StyledBoxWrapperDiv = styled.div`
  position: relative;
  min-width: 0px;

  > div > div {
    margin: 0px;
  }
`;

const StyledCalendarSectionWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 8px;

  div {
    min-width: 0px;
    padding: 0;
  }
`;

const StyledMoreOptionsSectionWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 5px;
`;

const StyledHtmlEditorBoxContainer = styled(HtmlEditorBoxContainer)`
  min-height: 70px;
  word-break: break-word;
`;

const StyledCalendarShowAsDropdownBox = styled(CalendarShowAsDropdownBox)`
  width: 135px;
`;

const StyledCalendarVisibilityDropdownBox = styled(CalendarVisibilityDropdownBox)`
  width: 150px;
`;

const StyledTextDiv = styled.div`
  line-height: 21px;
  white-space: pre-wrap;
  min-width: 0px;
  overflow: hidden;
  ${textSelection()}
`;

/*
 * Component.
 */

enum RecurringEventSaveTypesEnum {
  ENTIRE_SERIES = 'entire_series',
  INSTANCE = 'instance',
}

interface SaveOptions {
  hasConfirmedSave?: boolean;
  recurringSaveType?: RecurringEventSaveTypesEnum;
}

export const CalendarEventEditor: FC<CalendarEventEditorProps> = (props) => {
  const theme = useUserTheme();
  const localize = useLocalize();

  const {
    viewingNowCalendars,
    calendarEventData,
    isEventOrganizer,
    visualParticipants,
    instantReschedulingLink,
    hasInstantReschedulingLinkFF,
    isInstantReschedulingEnabledPreference,
    onSaveClick,
    fetchOrCreateInstantReschedulingLinkAsync,
  } = props;

  const {calendarEventVersion, calendarEvent, calendar, calendarVersion, channel} = calendarEventData;
  const isUnsavedDraft = isUnsavedDraftCalendarEventVersion(calendarEventVersion);
  const attendeesCanInviteOthers =
    calendarEvent.attendeesCanInviteOthers && calendar.behaviors.supportsAttendeesCanInviteOthers;

  const [shouldShowSaveWarning, toggleSaveWarning] = useToggle(false);
  const [shouldShowRecurringSaveDialog, toggleRecurringSaveDialog] = useToggle(false);

  const [saveOptions, setSaveOptions] = useState<SaveOptions>({
    hasConfirmedSave: false,
    recurringSaveType: undefined,
  });

  // Summary.
  const initialSummary = calendarEventVersion.summary || '';
  const [summary, setSummary] = useState(initialSummary);

  // Participants.
  const [nonResourceParticipants, setNonResourceParticipants] = useState<
    ReadonlyArray<VisualParticipantModel>
  >(visualParticipants.filter((p) => p.type !== ParticipantTypesEnum.RESOURCE));
  const [resourceParticipants, setResourceParticipants] = useState<ReadonlyArray<VisualParticipantModel>>(
    visualParticipants.filter((p) => p.type === ParticipantTypesEnum.RESOURCE),
  );
  const [initialViewingNowCalendars] = useState<ReadonlyArray<ViewingNowCalendar>>(viewingNowCalendars);
  const [initialNonResourceParticipants] =
    useState<ReadonlyArray<VisualParticipantModel>>(nonResourceParticipants);

  useInitialEffect(() => {
    if (hasInstantReschedulingLinkFF && !instantReschedulingLink) {
      fetchOrCreateInstantReschedulingLinkAsync(calendarVersion.id);
    }
  });

  // instant reschedule
  const [isInstantReschedulingEnabled, toggleIsInstantReschedulingEnabled] = useToggle(
    isInstantReschedulingEnabledPreference,
  );
  const [newLinkDialogPanelId, setNewLinkDialogPanelId] = useState<CalendarsPanelId>();
  const onNewLinkClick = () => setNewLinkDialogPanelId(makeCalendarsPanelId());
  const onNewLinkDialogClose = () => setNewLinkDialogPanelId(undefined);

  // Location.
  const initialLocation = buildCalendarEventVersionLocationText(calendarEventVersion);
  const [location, setLocation] = useState(initialLocation);

  // Conference.
  const [conference, setConference] = useState<ConferenceModel | undefined>(calendarEventVersion.conference);

  // Calendar.
  const [selectedCalendarData, setSelectedCalendarData] = useState({calendarVersion, calendar});
  const onSelectedCalendarDataChange = (newSelectedCalendarData: SelectedCalendarData) => {
    // If we've set a provider conference, but that conference type is not supported
    // by the currently selected calendar, clear the conference.
    if (
      conference &&
      isProviderConferenceType(conference.type) &&
      !newSelectedCalendarData.calendar.supportedProviderConferenceTypes.includes(conference.type)
    ) {
      setConference(undefined);
    }

    setSelectedCalendarData(newSelectedCalendarData);
  };

  // Color.
  const [{color, colorHasBeenEdited}, setColor] = useState({
    color: makeCalendarEventVersionColor(calendarEventVersion, calendarVersion),
    colorHasBeenEdited: false,
  });

  const onColorChange = (newColor: PaletteColorsEnum) => {
    setColor({color: newColor, colorHasBeenEdited: true});
    if (props.onFocusedEventOverridesChange) {
      props.onFocusedEventOverridesChange({color: newColor});
    }
  };

  // Reminders.
  const [reminders, setReminders] = useState(
    Array.isArray(calendarEventVersion.reminders)
      ? calendarEventVersion.reminders
      : calendarVersion.reminders,
  );

  // Availability/Visibility.
  const [showAs, setShowAs] = useState(calendarEventVersion.showAs);
  const [visibility, setVisibility] = useState(calendarEventVersion.visibility);
  const [hasChangedShowAs, setHasChangedShowAs] = useState(false);

  const onShowAsChange = (showAsType: CalendarEventVersionShowAsTypesEnum) => {
    setHasChangedShowAs(true);
    setShowAs(showAsType);
  };

  // Start/End.
  const {instanceStartDate} = calendarEventData;
  const instanceStartDateTime = instanceStartDate ? DateTime.fromMillis(instanceStartDate) : undefined;
  const {startDateTime, endDateTime} = makeStartEndDateTimeForCalendarEventVersion(
    calendarEventVersion,
    instanceStartDateTime,
  );
  const [startEndData, setStartEndData] = useState({
    startDateTime,
    endDateTime,
    isAllDay: calendarEventVersion.isAllDay,
  });
  const onStartEndDataChange = (data: StartEndData) => {
    // When isAllDay changes for unsaved events where showAs hasn't been touched, update the showAs
    // to the default state
    const {isAllDay: wasAllDay} = startEndData;
    const {isAllDay} = data;
    if (isUnsavedDraft && !hasChangedShowAs && wasAllDay !== isAllDay) {
      setShowAs(buildDefaultShowAsForIsAllDay(isAllDay));
    }

    setStartEndData(data);

    if (props.onFocusedEventOverridesChange) {
      props.onFocusedEventOverridesChange({startEnd: buildCalendarEventVersionStartEnd(data)});
    }
  };

  // If the user changes the startEnd by dragging a region of a calendar grid, update our state.
  const {overrides} = props.calendarEventData;
  useEffect(() => {
    if (!(overrides && overrides.startEnd)) {
      return;
    }

    const newStartDateTime = makeCalendarEventVersionDateTime(
      overrides.startEnd,
      CalendarEventTimeTypesEnum.START,
    );
    const newEndDateTime = makeCalendarEventVersionDateTime(
      overrides.startEnd,
      CalendarEventTimeTypesEnum.END,
    );

    setStartEndData({
      startDateTime: newStartDateTime,
      endDateTime: newEndDateTime,
      isAllDay: overrides.startEnd.isAllDay,
    });
  }, [overrides]);

  const hasChangedStartEnd =
    !startDateTime.equals(startEndData.startDateTime) ||
    !endDateTime.equals(startEndData.endDateTime) ||
    calendarEventVersion.isAllDay !== startEndData.isAllDay;

  // Recurrence
  const [recurrence, setRecurrence] = useState(calendarEventVersion.recurrence);
  // We disable editing a recurrence for:
  // - An existing series master event for which hasChangedStartEnd = true. Since
  //   we don't allow editing start/end for an entire series, we disable the
  //   recurrence whenever the start/end is changed.
  // - A series exception event.
  // - Non-organizers, since recurrence is a global property that providers
  //   don't allow participants to edit.
  const shouldDisableRecurrence =
    (!isUnsavedDraft && isRecurringMasterEvent(calendarEvent) && hasChangedStartEnd) ||
    isRecurringExceptionEvent(calendarEvent) ||
    !isEventOrganizer;
  const hasChangedRecurrence = !_.isEqual(recurrence, calendarEventVersion.recurrence);

  const masterRecurrence = props.masterCalendarEventVersion
    ? props.masterCalendarEventVersion.recurrence
    : undefined;

  useEffect(() => {
    // If the recurrence is disabled, reset it to its initial value.
    if (shouldDisableRecurrence) {
      setRecurrence(calendarEventVersion.recurrence);
    }
  }, [shouldDisableRecurrence, calendarEventVersion.recurrence]);

  const channelType = channel ? channel.type : undefined;
  const initialDescription = useMemo(
    () => buildCalendarEventDescriptionHtml(calendarEventVersion.description, channelType),
    [calendarEventVersion.description, channelType],
  );
  const [hasDescriptionChanged, setHasDescriptionChanged] = useState(false);
  const onDescriptionChange = () => setHasDescriptionChanged(true);

  const editorRef = useRef<HtmlEditorBoxHandle>(null);

  const onSaveButtonClick = (saveOverrides?: SaveOptions) => {
    // Check if we need to prompt the user for a recurringSaveType.
    const recurringSaveType =
      (saveOverrides && saveOverrides.recurringSaveType) || saveOptions.recurringSaveType;

    const shouldPromptForRecurringSaveType = isRecurringSaveTypeRequired(calendarEvent) && !recurringSaveType;
    // Only show prompt if:
    // - we haven't changed start/end for now, since we don't support those series modifications yet.
    // - we haven't changed recurrence, since we don't support modifying both the series master
    // recurrence and creating an instance simulataneously.
    if (shouldPromptForRecurringSaveType && !hasChangedStartEnd && !hasChangedRecurrence) {
      toggleRecurringSaveDialog();
      return;
    }

    // Combine the options.
    const recurringSaveTypeOverride = shouldPromptForRecurringSaveType
      ? getRecurringSaveTypeOverride(hasChangedStartEnd, hasChangedRecurrence)
      : {};

    const options = {
      ...saveOptions,
      ...saveOverrides,
      ...recurringSaveTypeOverride,
    };

    const description = descriptionFromModelOrEditor(calendarEventVersion, editorRef.current);

    // Show save warning if we're about to make changes that would only be reflected on our calendar.
    // Conditions are: 1) we're not the event organizer 2) we've made changes to the topic/date/location/description.
    const hasEditedGlobalSettings =
      summary !== initialSummary ||
      location !== initialLocation ||
      !startEndData.startDateTime.equals(startDateTime) ||
      !startEndData.endDateTime.equals(endDateTime) ||
      startEndData.isAllDay !== calendarEventVersion.isAllDay ||
      hasDescriptionChanged;

    if (hasEditedGlobalSettings && !isEventOrganizer && !options.hasConfirmedSave) {
      toggleSaveWarning();
      return;
    }

    // If we apply a change to the entire recurring series, we should preserve the startEnd of the master event.
    const newStartEnd =
      options.recurringSaveType === RecurringEventSaveTypesEnum.ENTIRE_SERIES
        ? {}
        : buildCalendarEventVersionStartEnd(startEndData);

    const {color: newColor, colorIsNop} = buildCalendarEventVersionColor(
      color,
      colorHasBeenEdited,
      selectedCalendarData,
    );

    const newCalendarEventVersion = {
      ...calendarEventVersion,
      ...newStartEnd,
      includesTeammateReschedulingLink:
        isInstantReschedulingEnabled &&
        Boolean(instantReschedulingLink) &&
        hasAnyParticipantsExcludeGlobalOrganizer(nonResourceParticipants) &&
        isUnsavedDraft,
      summary,
      description,
      locations: location ? [{displayName: location}] : [],
      color: newColor,
      conference: conference && isProviderConferenceType(conference.type) ? conference : undefined,
      visibility,
      showAs,
      recurrence,
      reminders: buildCalendarEventVersionRemindersToSave(
        selectedCalendarData.calendarVersion.reminders,
        calendarEventVersion.reminders,
        reminders,
      ),
      calendarVersionIds: isUnsavedDraft
        ? [selectedCalendarData.calendarVersion.id]
        : calendarEventVersion.calendarVersionIds,
      isSaving: true,
    };

    // If we're creating a new event, we have to ensure that we include the organizer as a participant (they may not be attendee).
    const newVisualParticipants = [...resourceParticipants, ...nonResourceParticipants];
    const completeVisualParticipants = isUnsavedDraft
      ? addOrganizerToVisualParticipants(
          newVisualParticipants,
          selectedCalendarData.calendar.organizerAddress,
        )
      : newVisualParticipants;

    onSaveClick({
      newCalendarEventVersion,
      newCalendarEvent: calendarEvent,
      calendarVersion: selectedCalendarData.calendarVersion,
      visualParticipants: completeVisualParticipants,
      instanceStartDate,
      recurringSaveType: options.recurringSaveType,
      colorIsNop,
    });
  };

  const onConferenceChange = (newConference: ConferenceModel | undefined) => {
    const editor = editorRef.current;
    if (!editor) {
      throw new Error('Missing calendar event description HTML editor box handle.');
    }

    if (!newConference) {
      // No cleanup needed if no conference or if the conference does not correspond to an integration.
      if (!conference || isProviderConferenceType(conference.type)) {
        setConference(undefined);
        return;
      }

      // Remove the conference URL from the location, accounting for commas.
      const conferenceUrl = tryFindConferenceJoinUrl(conference);
      if (conferenceUrl) {
        const newLocation = location
          .replace(`, ${conferenceUrl}`, '')
          .replace(`${conferenceUrl}, `, '')
          .replace(conferenceUrl, '');

        setLocation(newLocation);
      }
      editor.setCleanHtml('');
      setConference(undefined);
      return;
    }

    // Set the new conference and update the location/description if this conference
    // corresponds to an integration.
    setConference(newConference);

    if (!isProviderConferenceType(newConference.type)) {
      const url = tryFindConferenceJoinUrl(newConference);
      if (url) {
        setLocation(location ? `${location}, ${url}` : url);
      }

      if (newConference.description) {
        const trimmedConferenceDescription = newConference.description.trim();
        const conferenceDescriptionHtml = isHtml(trimmedConferenceDescription)
          ? trimmedConferenceDescription
          : textToHtml(trimmedConferenceDescription);
        const eventDescriptionHtml = editor.isEmpty ? undefined : editor.cleanHtml;

        editor.setCleanHtml(addConferenceToEventDescription(eventDescriptionHtml, conferenceDescriptionHtml));
      }
    }
  };

  const canInviteParticipants =
    isEventEditable(
      selectedCalendarData.calendar,
      selectedCalendarData.calendarVersion,
      calendarEventVersion,
    ) &&
    (isEventOrganizer || attendeesCanInviteOthers);

  const canEditGlobalSettings = calendarCanEditGlobalSettings(
    selectedCalendarData.calendar,
    true,
    isEventOrganizer,
  );

  return (
    <StyledLayoutDiv>
      {renderHeader(props, isUnsavedDraft, initialViewingNowCalendars)}
      <DynamicScrollBoundaries hasTopBoundary hasBottomBoundary color={theme.greys.shade30}>
        <StyledWrapperDiv>
          {renderSummaryInput(localize, props, summary, setSummary, !canEditGlobalSettings)}
          {renderDateSection(
            startEndData,
            onStartEndDataChange,
            props.timeFormat,
            calendarEventVersion,
            instanceStartDate,
            masterRecurrence,
            recurrence,
            setRecurrence,
            shouldDisableRecurrence,
            !canEditGlobalSettings,
          )}
          {renderParticipantsSection(
            props,
            initialNonResourceParticipants,
            nonResourceParticipants,
            setNonResourceParticipants,
            canInviteParticipants,
            selectedCalendarData,
            isUnsavedDraft,
          )}
          {maybeRenderInstantReschedulingCheckBox(
            nonResourceParticipants,
            isInstantReschedulingEnabled,
            isUnsavedDraft,
            instantReschedulingLink,
            hasInstantReschedulingLinkFF,
            toggleIsInstantReschedulingEnabled,
            onNewLinkClick,
          )}
          {newLinkDialogPanelId &&
            renderInstantReschedulingDialog(
              props,
              newLinkDialogPanelId,
              nonResourceParticipants,
              instantReschedulingLink,
              onNewLinkDialogClose,
            )}
          {renderLocationSection(localize, props, location, setLocation, !canEditGlobalSettings)}
          {renderMeetingRoomsSection(
            props,
            selectedCalendarData.calendarVersion,
            selectedCalendarData.calendar,
            resourceParticipants,
            setResourceParticipants,
            location,
            setLocation,
            canInviteParticipants,
            startEndData,
          )}
          {/* Same calendar section as below, but putting it behind a FF for now */}
          {props.hasDefaultVideoConferencing &&
            renderCalendarSection(
              selectedCalendarData,
              onSelectedCalendarDataChange,
              color,
              onColorChange,
              isUnsavedDraft,
            )}
          {canEditGlobalSettings &&
            renderConferenceSection(
              selectedCalendarData.calendarVersion,
              selectedCalendarData.calendar,
              conference,
              onConferenceChange,
              startEndData,
              summary,
              isUnsavedDraft,
            )}
          {canEditGlobalSettings &&
            renderDescriptionSection(editorRef, initialDescription, onDescriptionChange)}
          {!props.hasDefaultVideoConferencing &&
            renderCalendarSection(
              selectedCalendarData,
              onSelectedCalendarDataChange,
              color,
              onColorChange,
              isUnsavedDraft,
            )}
          {renderRemindersSection(reminders, setReminders)}
          {renderMoreOptionsSection(selectedCalendarData, showAs, onShowAsChange, visibility, setVisibility)}
          {maybeRenderRecurringSavePrompt(
            shouldShowRecurringSaveDialog,
            toggleRecurringSaveDialog,
            setSaveOptions,
            onSaveButtonClick,
          )}
          {maybeRenderSaveWarning(shouldShowSaveWarning, toggleSaveWarning, onSaveButtonClick)}
        </StyledWrapperDiv>
      </DynamicScrollBoundaries>
      {renderFooter(onSaveButtonClick, props, initialViewingNowCalendars)}
    </StyledLayoutDiv>
  );
};

export function renderInstantReschedulingDialog(
  props: CalendarEventEditorProps,
  newLinkDialogPanelId: CalendarsPanelId,
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  instantReschedulingLink: SchedulingLinkModel | undefined,
  onNewLinkDialogClose: Handler,
) {
  if (!instantReschedulingLink) {
    return null;
  }

  return (
    <InstantReschedulingLinkDialogContainer
      panelId={newLinkDialogPanelId}
      parentPanelId={props.panelId}
      visualParticipants={nonResourceParticipants}
      schedulingLinkId={instantReschedulingLink.id}
      onDialogClose={onNewLinkDialogClose}
    />
  );
}

export function maybeRenderInstantReschedulingCheckBox(
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  isInstantReschedulingEnabled: boolean,
  isUnsavedDraft: boolean,
  instantReschedulingLink: SchedulingLinkModel | undefined,
  hasInstantReschedulingLinkFF: boolean,
  toggleIsInstantReschedulingEnabled: HandlerOf<boolean>,
  onNewLinkClick: Handler,
) {
  if (
    !hasInstantReschedulingLinkFF ||
    !isUnsavedDraft ||
    !hasAnyParticipantsExcludeGlobalOrganizer(nonResourceParticipants)
  ) {
    return null;
  }

  return (
    <StyledInstantReschedulingWrapper>
      <StyledHeaderSettingsDiv>
        <CheckBox
          isDisabled={!instantReschedulingLink}
          isChecked={instantReschedulingLink && isInstantReschedulingEnabled}
          onChange={toggleIsInstantReschedulingEnabled}
        />
        <StyledInstantReschedulingTitleDiv>
          <Trans id="calendar-event-instant-reschedule-title">Guests can reschedule</Trans>
          <StyledInstantReschedulingToolTipDiv>
            <TooltipCoordinator
              render={() => (
                <Trans id="calendar-instant-reschedule-tooltip">
                  Include a rescheduling link in the meeting description to allow guests to reschedule to
                  another available time.
                </Trans>
              )}
            />
          </StyledInstantReschedulingToolTipDiv>
        </StyledInstantReschedulingTitleDiv>
      </StyledHeaderSettingsDiv>
      <StyledSetAvailableTimesWrapperDiv>
        <Button
          isDisabled={!instantReschedulingLink}
          variant={ButtonVariantsEnum.LINK}
          onClick={onNewLinkClick}
        >
          <Trans id="calendar-instant-reschedule-set-available">Set available times</Trans>
        </Button>
      </StyledSetAvailableTimesWrapperDiv>
    </StyledInstantReschedulingWrapper>
  );
}

export function hasAnyParticipantsExcludeGlobalOrganizer(
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  if (nonResourceParticipants.length === 0) {
    return false;
  }
  return nonResourceParticipants.filter((p) => p.namespace !== 'global').length > 0;
}

export function getRecurringSaveTypeOverride(hasChangedStartEnd: boolean, hasChangedRecurrence: boolean) {
  if (hasChangedStartEnd) {
    return {recurringSaveType: RecurringEventSaveTypesEnum.INSTANCE};
  }

  if (hasChangedRecurrence) {
    return {recurringSaveType: RecurringEventSaveTypesEnum.ENTIRE_SERIES};
  }

  return {};
}

export function addConferenceToEventDescription(
  eventDescription: string | undefined,
  conferenceDescription: string,
) {
  const separator = '<div>──────────</div>';
  const separatedConferenceDescription = `${separator}${conferenceDescription}${separator}`;

  // If the event description is empty, return the conference description.
  if (!eventDescription) {
    return separatedConferenceDescription;
  }

  // If the event has an existing conference description,
  // replace it with the new one.
  const splitEventDescription = eventDescription.split(separator);
  if (splitEventDescription.length === 3) {
    // splitEventDescription[1] is expected to be the old conference description
    return `${splitEventDescription[0]}${separatedConferenceDescription}${splitEventDescription[2]}`;
  }

  // If the event has no conference description, append
  // the new conference description to it.
  return `${eventDescription}${separatedConferenceDescription}`;
}

export function descriptionFromModelOrEditor(
  calendarEventVersion: CalendarEventVersionModel,
  editor: HtmlEditorBoxHandle | null,
) {
  if (editor) {
    return editor.isEmpty ? '' : editor.cleanHtml;
  }

  return calendarEventVersion.description;
}

export function renderHeader(
  props: CalendarEventEditorProps,
  isUnsavedDraft: boolean,
  initialViewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
) {
  const headerTitle = isUnsavedDraft ? (
    <Trans id="calendar-header-newevent">New event</Trans>
  ) : (
    <Trans id="calendar-header-editevent">Edit event</Trans>
  );

  return (
    <SideCalendarsHeader title={headerTitle} onBackClick={props.onBackClick}>
      {maybeRenderCloseButton(props, initialViewingNowCalendars)}
    </SideCalendarsHeader>
  );
}

export function maybeRenderCloseButton(
  props: CalendarEventEditorProps,
  initialViewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
) {
  if (!props.onRequestClose) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <IconButton
          iconName="close"
          onClick={() =>
            props.onCalendarEventEditorDone({
              viewingNowCalendarsToResetTo: initialViewingNowCalendars,
              finalHandler: props.onRequestClose,
            })
          }
          label={<Trans id="calendar-header-close">Close</Trans>}
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderSummaryInput(
  localize: Localizer,
  props: CalendarEventEditorProps,
  summary: string,
  setSummary: HandlerOf<string>,
  isDisabled: boolean,
) {
  return (
    <StyledBoxWrapperDiv>
      <TextBox
        size={VisualSizesEnum.LARGE}
        placeholder={localize(t('eventeditor-addtitle')`Add a title`)}
        value={summary}
        onChange={setSummary}
        onBlur={() => {
          if (props.onFocusedEventOverridesChange) {
            props.onFocusedEventOverridesChange({summary});
          }
        }}
        shouldAutoFocus
        isDisabled={isDisabled}
      />
    </StyledBoxWrapperDiv>
  );
}

export function renderDateSection(
  startEndData: StartEndData,
  setStartEndData: HandlerOf<StartEndData>,
  timeFormat: TimeFormatsEnum,
  calendarEventVersion: CalendarEventVersionModel,
  instanceStartDate: number | undefined,
  masterRecurrence: Readonly<RecurrenceModel> | undefined,
  recurrence: Readonly<RecurrenceModel> | undefined,
  setRecurrence: HandlerOf<RecurrenceModel | undefined>,
  isRecurrenceDisabled: boolean,
  isDisabled: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledDateSectionWrapperDiv>
          <CalendarEventDetailRow
            iconContent={<StyledSectionIcon name="snoozeSmall" color={theme.greys.shade70} />}
            content={
              isDisabled ? (
                <StyledTextDiv>
                  <CalendarEventTimeFormatter
                    timeFormat={timeFormat}
                    calendarEventVersion={calendarEventVersion}
                    instanceStartDate={instanceStartDate}
                    shouldUseMultiline
                  />
                </StyledTextDiv>
              ) : (
                <CalendarEventStartEndEditor
                  timeFormat={timeFormat}
                  startDateTime={startEndData.startDateTime}
                  endDateTime={startEndData.endDateTime}
                  isAllDay={startEndData.isAllDay}
                  masterRecurrence={masterRecurrence}
                  recurrence={recurrence}
                  onRecurrenceChange={isRecurrenceDisabled ? undefined : setRecurrence}
                  onChange={setStartEndData}
                />
              )
            }
          />
        </StyledDateSectionWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

export function renderParticipantsSection(
  props: CalendarEventEditorProps,
  initialNonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  setNonResourceParticipants: HandlerOf<ReadonlyArray<VisualParticipantModel>>,
  canInviteParticipants: boolean,
  selectedCalendarData: SelectedCalendarData,
  isUnsavedDraft: boolean,
) {
  // If we have no existing participants and can't invite new ones, nothing to render.
  if (nonResourceParticipants.length === 0 && !canInviteParticipants) {
    return null;
  }

  const {calendarEventData} = props;

  const onVisualParticipantsChange = (newParticipants: ReadonlyArray<VisualParticipantModel>) => {
    if (props.onVisualParticipantsChange) {
      props.onVisualParticipantsChange(nonResourceParticipants, newParticipants);
    }

    // Add the organizer to attendees on adding the first attendee, if it's a primary calendarVersion
    const shouldAddOrganizer =
      props.isEventOrganizer &&
      nonResourceParticipants.every((p) => !p.isAttendee) &&
      selectedCalendarData.calendarVersion.isPrimary;

    // If we add/remove the same participant in an already existing event before saving, we should retain the status/state of that participant.
    const mergedNewParticipants = newParticipants.map((newParticipant) => {
      const maybeInitialParticipant = initialNonResourceParticipants.find(
        (initialParticipant) => initialParticipant.contact.handle === newParticipant.contact.handle,
      );
      return maybeInitialParticipant ?? newParticipant;
    });

    const newVisualParticipants = shouldAddOrganizer
      ? addOrganizerToVisualParticipants(
          newParticipants,
          selectedCalendarData.calendar.organizerAddress,
          true,
        )
      : mergedNewParticipants;

    return setNonResourceParticipants(newVisualParticipants);
  };

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="teammatesSmall" color={theme.greys.shade70} />}
          content={
            <CalendarEventParticipantsEditor
              visualParticipants={nonResourceParticipants}
              onVisualParticipantsChange={canInviteParticipants ? onVisualParticipantsChange : undefined}
              readonlyContactUids={
                props.isEventOrganizer
                  ? undefined
                  : calendarEventData.calendarEvent.participants.map((p) => p.contactUid)
              }
              panelId={props.panelId}
              organizerAddress={calendarEventData.calendar.organizerAddress}
              isUnsavedDraft={isUnsavedDraft}
              onViewGuestAvailabilityClick={props.onViewGuestAvailabilityClick}
            />
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderLocationSection(
  localize: Localizer,
  props: CalendarEventEditorProps,
  location: string,
  setLocation: HandlerOf<string>,
  isDisabled: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="locationSmall" color={theme.greys.shade70} />}
          content={
            <StyledBoxWrapperDiv>
              <TextBox
                size={VisualSizesEnum.MEDIUM}
                placeholder={localize(t('cal-event-editor-add-location')`Add a location`)}
                value={location}
                onChange={setLocation}
                isDisabled={isDisabled}
              />
            </StyledBoxWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderMeetingRoomsSection(
  props: CalendarEventEditorProps,
  calendarVersion: CalendarVersionModel,
  calendar: CalendarModel,
  resourceParticipants: ReadonlyArray<VisualParticipantModel>,
  setResourceParticipants: HandlerOf<ReadonlyArray<VisualParticipantModel>>,
  location: string,
  setLocation: HandlerOf<string>,
  canInviteParticipants: boolean,
  startEndData: StartEndData,
) {
  const {participants} = props.calendarEventData.calendarEvent;
  const {channelId} = calendarVersion;

  if (!channelId) {
    return null;
  }

  // If we have no existing rooms and this calendar does not support adding new ones, don't render anything.
  if (resourceParticipants.length === 0 && !canInviteParticipants) {
    return null;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="meetingBuildingSmall" color={theme.greys.shade70} />}
          content={
            <MeetingRoomsEditor
              panelId={props.panelId}
              visualParticipants={resourceParticipants}
              onVisualParticipantsChange={canInviteParticipants ? setResourceParticipants : undefined}
              location={location}
              onLocationChange={props.isEventOrganizer ? setLocation : undefined}
              readonlyContactUids={props.isEventOrganizer ? undefined : participants.map((p) => p.contactUid)}
              startEndData={startEndData}
              channelId={channelId}
              calendar={calendar}
            />
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderConferenceSection(
  calendarVersion: CalendarVersionModel,
  calendar: CalendarModel,
  conference: ConferenceModel | undefined,
  onConferenceChange: HandlerOf<ConferenceModel | undefined>,
  startEndData: StartEndData,
  summary: string,
  isUnsavedDraft: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="videoSmall" color={theme.greys.shade70} />}
          content={
            <ConferenceEditor
              supportedProviderConferenceTypes={calendar.supportedProviderConferenceTypes}
              conference={conference}
              onConferenceChange={onConferenceChange}
              startEndData={startEndData}
              summary={summary}
              calendarVersionId={calendarVersion.id}
              isUnsavedDraft={isUnsavedDraft}
            />
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderDescriptionSection(
  editorRef: RefObject<HtmlEditorBoxHandle>,
  initialDescription: string,
  onChange: Handler,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="notesSmall" color={theme.greys.shade70} />}
          content={
            <StyledBoxWrapperDiv>
              <StyledHtmlEditorBoxContainer
                ref={editorRef}
                initialCleanHtml={initialDescription}
                renderPlaceholder={() => (
                  <Trans id="cal-event-editor-add-description">Add a description</Trans>
                )}
                hasToolbar
                onChange={onChange}
              />
            </StyledBoxWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderCalendarSection(
  selectedCalendarData: SelectedCalendarData,
  onSelectedCalendarDataChange: HandlerOf<SelectedCalendarData>,
  color: PaletteColorsEnum,
  setColor: HandlerOf<PaletteColorsEnum>,
  isUnsavedDraft: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="calendarSmall" color={theme.greys.shade70} />}
          content={
            <StyledCalendarSectionWrapperDiv>
              <CalendarDropdownBoxContainer
                selectedCalendarVersion={selectedCalendarData.calendarVersion}
                onCalendarDataSelect={(data) => {
                  onSelectedCalendarDataChange(data);
                  setColor(data.calendarVersion.color);
                }}
                isDisabled={!isUnsavedDraft}
              />
              <CalendarColorDropdownBox color={color} onColorSelect={setColor} />
            </StyledCalendarSectionWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderRemindersSection(
  reminders: ReadonlyArray<CalendarEventVersionReminder>,
  setReminders: HandlerOf<ReadonlyArray<CalendarEventVersionReminder>>,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="subscribeSmall" color={theme.greys.shade70} />}
          content={<CalendarRemindersEditor reminders={reminders} onRemindersChange={setReminders} />}
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderMoreOptionsSection(
  selectedCalendarData: SelectedCalendarData,
  showAs: CalendarEventVersionShowAsTypesEnum,
  onShowAsChange: HandlerOf<CalendarEventVersionShowAsTypesEnum>,
  visibility: CalendarEventVersionVisibilityTypesEnum,
  setVisibility: HandlerOf<CalendarEventVersionVisibilityTypesEnum>,
) {
  const {calendar, calendarVersion} = selectedCalendarData;
  // Outlook does not support creating private from non-owner calendars.
  const calendarSupportsPrivateEvents =
    calendarVersion.accessRole === CalendarVersionAccessRolesEnum.OWNER ||
    calendar.behaviors.writerCanEditPrivateEvents;

  const shouldHidePublicVisibility = calendar.provider === CalendarProviderTypesEnum.OFFICE365;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="eyeSmall" color={theme.greys.shade70} />}
          content={
            <StyledMoreOptionsSectionWrapperDiv>
              <StyledCalendarShowAsDropdownBox showAs={showAs} onShowAsChange={onShowAsChange} />
              <StyledCalendarVisibilityDropdownBox
                visibility={visibility}
                onVisibilityChange={setVisibility}
                shouldDisablePrivateVisibility={!calendarSupportsPrivateEvents}
                shouldHidePublicVisibility={shouldHidePublicVisibility}
              />
            </StyledMoreOptionsSectionWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function renderFooter(
  onSaveClick: Handler,
  props: CalendarEventEditorProps,
  initialViewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
) {
  return (
    <StyledFooterDiv>
      <Button
        size={VisualSizesEnum.MEDIUM}
        variant={ButtonVariantsEnum.SECONDARY}
        onClick={() =>
          props.onCalendarEventEditorDone({
            viewingNowCalendarsToResetTo: initialViewingNowCalendars,
            finalHandler: props.onBackClick || props.onRequestClose,
          })
        }
      >
        Cancel
      </Button>
      <Button
        size={VisualSizesEnum.MEDIUM}
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={() => onSaveClick()}
      >
        Save
      </Button>
    </StyledFooterDiv>
  );
}

export function maybeRenderSaveWarning(
  shouldShowSaveWarning: boolean,
  toggleSaveWarning: Handler,
  onSaveClick: HandlerOf<SaveOptions>,
) {
  if (!shouldShowSaveWarning) {
    return null;
  }

  return (
    <OkCancelDialog
      title={<Trans id="cal-editor-are-you-sure">Are you sure?</Trans>}
      okButtonLabel={<Trans id="cal-editor-ok">Ok</Trans>}
      cancelButtonLabel={<Trans id="cal-editor-cancel">Cancel</Trans>}
      onOkClick={() => onSaveClick({hasConfirmedSave: true})}
      onRequestClose={() => toggleSaveWarning()}
    >
      <Trans id="cal-editor-organizer-warning">
        These changes will only be reflected on your calendar because you&apos;re not the event organizer.
      </Trans>
    </OkCancelDialog>
  );
}

export function maybeRenderRecurringSavePrompt(
  shouldShowRecurringSaveDialog: boolean,
  toggleRecurringSaveDialog: Handler,
  setSaveOptions: HandlerOf<SaveOptions>,
  onSaveClick: HandlerOf<SaveOptions>,
) {
  if (!shouldShowRecurringSaveDialog) {
    return null;
  }

  return (
    <RecurringUpdateDialog
      onConfirmClick={(recurringSaveType) => {
        toggleRecurringSaveDialog();
        setSaveOptions({recurringSaveType});
        onSaveClick({recurringSaveType});
      }}
      onCancelClick={() => toggleRecurringSaveDialog()}
    />
  );
}
